import { useCallback, useMemo, useState } from 'react';
import { LocaleContext, findLocaleMeta } from './LocaleContext';
import { DEFAULT_LOCALE, Locale, LocaleMeta } from './types';

interface LocaleProviderProps {
  children: React.ReactNode;
  initialLocale?: Locale;
}

export function LocaleProvider({ children, initialLocale = DEFAULT_LOCALE }: LocaleProviderProps) {
  const [locale, setLocale] = useState<Locale>(initialLocale);

  const meta = useMemo<LocaleMeta>(() => findLocaleMeta(locale), [locale]);

  const handleSetLocale = useCallback((next: Locale) => {
    setLocale(next);
    document.documentElement.lang = next;
    document.documentElement.dir = findLocaleMeta(next).direction;
  }, []);

  const toggleLocale = useCallback(() => {
    handleSetLocale(locale === 'en-US' ? 'ar-SA' : 'en-US');
  }, [locale, handleSetLocale]);

  const value = useMemo(
    () => ({
      ...meta,
      setLocale: handleSetLocale,
      toggleLocale,
    }),
    [meta, handleSetLocale, toggleLocale],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}
